export type CompanyArchiveFields = {
  id?: string | null;
  name?: string | null;
  archived?: boolean | null;
  status?: string | null;
  accountStatus?: string | null;
  companyOwnerId?: string | null;
};

export const isArchivedCompany = (
  company?: CompanyArchiveFields | null,
): boolean => {
  if (!company) return false;
  if (company.archived === true) return true;
  if (company.status === 'archived') return true;
  if (company.accountStatus === 'archived') return true;
  return /\(\s*archive\s*\)/i.test(company.name || '');
};

/** "SoloDevelop (archive)" → "SoloDevelop"; null if the name is not an archive label. */
export const liveCompanyNameFromArchive = (name?: string | null): string | null => {
  if (!name) return null;
  const stripped = name.replace(/\s*\(\s*archive\s*\)\s*$/i, '').trim();
  if (!stripped || stripped === name.trim()) return null;
  return stripped;
};

export const pickPreferredCompany = <T extends CompanyArchiveFields>(
  companies: Array<T | null | undefined>,
): T | null => {
  const real = companies.filter((company): company is T => Boolean(company?.id));
  if (real.length === 0) return null;
  return real.find((company) => !isArchivedCompany(company)) ?? real[0];
};

export const mergeCompaniesById = <T extends CompanyArchiveFields>(
  groups: Array<Array<T | null | undefined> | undefined>,
): T[] => {
  const byId = new Map<string, T>();
  for (const group of groups) {
    for (const company of group || []) {
      if (company?.id && !byId.has(company.id)) {
        byId.set(company.id, company);
      }
    }
  }
  return [...byId.values()];
};

type IdentityRecord = {
  id?: string | null;
  companyId?: string | null;
};

export const pickRecordForCompany = <T extends IdentityRecord>(
  items: Array<T | null | undefined>,
  companyId: string | undefined,
  extraMatch?: (item: T) => boolean,
): T | null => {
  const real = items.filter((item): item is T => Boolean(item?.id));
  if (real.length === 0) return null;
  if (companyId) {
    const byCompany = real.find((item) => item.companyId === companyId);
    if (byCompany) return byCompany;
    if (extraMatch) {
      const extra = real.find(extraMatch);
      if (extra) return extra;
    }
    return null;
  }
  return real[0];
};

export const isMemberOfCompany = (
  company: CompanyArchiveFields | null | undefined,
  userItems: Array<{ companyId?: string | null } | null | undefined>,
  ownerItems: Array<{ id?: string | null; companyId?: string | null } | null | undefined>,
): boolean => {
  if (!company?.id) return false;
  if (userItems.some((item) => item?.companyId === company.id)) return true;
  if (ownerItems.some((item) => item?.companyId === company.id || item?.id === company.companyOwnerId)) {
    return true;
  }
  return false;
};

export const isUnlinkedInvite = (cognitoId?: string | null): boolean =>
  !cognitoId || cognitoId === '' || cognitoId.startsWith('pending-');

type InviteMatchFields = {
  id?: string | null;
  email?: string | null;
  name?: string | null;
};

export const findMatchingInviteUser = <T extends InviteMatchFields>(
  users: Array<T | null | undefined>,
  identity: { email?: string | null; name?: string | null },
): T | null => {
  const email = identity.email?.trim().toLowerCase();
  const name = identity.name?.trim().toLowerCase();
  const real = users.filter((item): item is T => Boolean(item?.id));
  if (email) {
    const byEmail = real.find((item) => item.email?.trim().toLowerCase() === email);
    if (byEmail) return byEmail;
  }
  if (name) {
    const byName = real.find((item) => item.name?.trim().toLowerCase() === name);
    if (byName) return byName;
  }
  return null;
};

export const mergeRecordsById = <T extends { id?: string | null }>(
  groups: Array<Array<T | null | undefined> | undefined>,
): T[] => {
  const byId = new Map<string, T>();
  for (const group of groups) {
    for (const item of group || []) {
      if (item?.id && !byId.has(item.id)) {
        byId.set(item.id, item);
      }
    }
  }
  return [...byId.values()];
};
